import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { StyledLinkBtn } from '../../Tasks/EventModalStyles';
import { HiPlusCircle, HiMinusCircle } from 'react-icons/hi';
import { fetchWrapper } from '@/_helpers';
import { Button, Form } from 'react-bootstrap';
import { evvApi } from '@/_services/api/evv';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import OurAccordion from '@/_elements/OurAccordion';

const EmployeePayRates = ({ employee, provider = {}, formType = 'edit', teamId }) => {
    const [fields, setFields] = useState([]);
    const [defaultServices, setDefultServices] = useState([]);
    const [loadingRates, setLoadingRates] = useState(false);
    const [savingRates, setSavingRates] = useState(false);
    const employeeId = employee?.id || employee?._id;

    const addField = () => {
        const newField = {
            id: Date.now(),
            staffRate: 0,
            service: null,
        };
        setFields((prev) => [...prev, newField]);
    };

    const deleteField = (id) => {
        const updatedFields = fields.filter((field) => field.id !== id);
        setFields(updatedFields);
    };
    const handleInputChange = (id, staffRate) => {
        const updatedFields = fields.map((field) => (field.id === id ? { ...field, staffRate } : field));
        setFields(updatedFields);
    };
    const handleServiceChange = (id, service) => {
        const updatedFields = fields.map((field) =>
            field.id === id
                ? {
                      ...field,
                      service: service
                          ? {
                                description: service.label.split(' - ')[1]?.trim(),
                                code: service.label.split(' - ').at(0)?.trim(),
                                id: service.value,
                            }
                          : null,
                  }
                : field
        );
        setFields(updatedFields);
    };
    const loadServices = (inputValue, callback = () => {}) => {
        setTimeout(async () => {
            const tempArray = [];
            try {
                if (provider?.state) {
                    const services = await fetchWrapper.get(
                        `evv/service?sortBy=name:asc&state=${provider?.state}&limit=200&${
                            inputValue ? `&HCPCS=${inputValue}` : ''
                        }${teamId ? `&team=${teamId}` : ''}`
                    );
                    services?.results?.forEach((element) => {
                        tempArray.push({
                            label: `${element.HCPCS} - ${element.Description}`,
                            value: element.id,
                        });
                    });
                }
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }
                toast.error(error?.message);
            } finally {
                callback(tempArray);
            }
        }, 1000);
    };

    useEffect(() => {
        loadServices('', setDefultServices);
    }, [provider]);

    useEffect(() => {
        const syncServiceLabels = () => {
            if (!defaultServices?.length) return;
            setFields((prev) =>
                prev.map((field) => {
                    const serviceId = field?.service?.id;
                    if (!serviceId || field?.service?.code) return field;
                    const option = defaultServices.find((item) => item.value === serviceId);
                    if (!option) return field;
                    const [code, description] = option.label.split(' - ');
                    return {
                        ...field,
                        service: {
                            ...field.service,
                            code: code?.trim(),
                            description: description?.trim(),
                        },
                    };
                })
            );
        };
        syncServiceLabels();
    }, [defaultServices]);

    useEffect(() => {
        const fetchPayRates = async () => {
            if (!employeeId) {
                setFields([]);
                return;
            }
            setLoadingRates(true);
            try {
                const rates = await evvApi.getEmployeeStaffRates(employeeId, { team: teamId });
                const mapped = (rates || []).map((rate) => ({
                    id: rate._id,
                    _id: rate._id,
                    staffRate: rate.staffRate ?? 0,
                    service: rate.service
                        ? {
                              id:
                                  typeof rate.service === 'string'
                                      ? rate.service
                                      : rate.service?.id || rate.service?._id,
                              code: rate.service?.code,
                              description: rate.service?.description,
                          }
                        : null,
                }));
                setFields(mapped);
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }
                toast.error(error?.message);
            } finally {
                setLoadingRates(false);
            }
        };
        fetchPayRates();
    }, [employeeId]);

    const handleSave = async () => {
        if (!employeeId || savingRates) return;
        const payload = fields.map((field) => ({
            _id: field._id,
            service: field.service.id,
            staffRate: Number(field.staffRate),
        }));
        setSavingRates(true);
        try {
            const saved = await evvApi.createOrUpdateEmployeeStaffRates(employeeId, payload, { team: teamId });
            const mapped = (saved || []).map((rate) => ({
                id: rate._id,
                _id: rate._id,
                staffRate: rate.staffRate ?? 0,
                service: rate.service
                    ? {
                          id: typeof rate.service === 'string' ? rate.service : rate.service?.id || rate.service?._id,
                          code: rate.service?.code,
                          description: rate.service?.description,
                      }
                    : null,
            }));
            toast.success('Saved staff rates successfully');
            setFields(mapped);
        } catch (error) {
            toast.error(error?.message || 'Something went wrong');
        } finally {
            setSavingRates(false);
        }
    };

    return (
        <div className="mt-4  border border-primary">
            <OurAccordion
                title={
                    <div>
                        <strong>Dynamic: Staff Pay Rates </strong>
                    </div>
                }
                defaultShow={false}
            >
                <div className="d-flex flex-column">
                    <div>
                        {fields?.map((field) => (
                            <div
                                key={field.id}
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '6px',
                                    margin: '6px 0',
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {/* Service row */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ minWidth: '70px' }}>Service : </span>
                                        <div style={{ flex: 1 }}>
                                            <AsyncSelect
                                                key={provider?.state}
                                                loadOptions={formType !== 'View' && loadServices}
                                                onChange={(service) => handleServiceChange(field.id, service)}
                                                value={
                                                    field?.service?.code
                                                        ? {
                                                              label: `${field.service.code} - ${field.service?.description}`,
                                                              value: field?.service?.id,
                                                          }
                                                        : field?.service?.id
                                                          ? defaultServices.find(
                                                                (item) => item.value === field?.service?.id
                                                            ) || null
                                                          : null
                                                }
                                                defaultOptions={defaultServices}
                                                isMulti={false}
                                                className="w-100"
                                                isDisabled={!provider?.state || formType === 'View' || savingRates}
                                            />
                                        </div>
                                    </div>

                                    {/* Pay Amount row */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ minWidth: '70px' }}>
                                            Pay Amount: <span className="text-danger">*</span>
                                        </span>
                                        <Form.Control
                                            type="number"
                                            step="1"
                                            style={{ flex: 1, height: '3.5rem' }}
                                            value={field.staffRate}
                                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                                            disabled={formType === 'View' || savingRates}
                                            required
                                            onWheel={(event) => event.currentTarget.blur()}
                                        />
                                    </div>

                                    {/* Remove button */}
                                    {formType !== 'View' && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <StyledLinkBtn
                                                className="danger"
                                                onClick={() => deleteField(field.id)}
                                                disabled={savingRates}
                                            >
                                                <HiMinusCircle />
                                                Remove
                                            </StyledLinkBtn>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {formType !== 'View' && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <StyledLinkBtn
                                    onClick={addField}
                                    style={{ width: 'max-content' }}
                                    disabled={savingRates}
                                >
                                    <HiPlusCircle />
                                    Add Staff Rate
                                </StyledLinkBtn>
                            </div>
                        )}

                        {formType !== 'View' && (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    onClick={handleSave}
                                    disabled={!employeeId || savingRates || loadingRates}
                                    type="button"
                                >
                                    {savingRates && <VscLoading className="spin mr-2" />}
                                    {'Save Staff Rates'}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </OurAccordion>
        </div>
    );
};

export default EmployeePayRates;
